import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Button,
    TablePagination,
    Slide,
    IconButton,
    Drawer,
    Divider,
    Chip,
    MenuItem,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {DataObjectOutlined} from '@mui/icons-material';
import {
    OperationLogService,
    OperationLogVO,
    OperationLogSessionVO,
    OperationLogSessionPage,
} from '@/services/manageService/operationLogService';
import {SnackbarField, InputField} from '@/components/common';
import OperationLogRuleList from '@/components/manage/sys/operation-log/operation-log-rule/OperationLogRuleList';
import {toolbarFilterFieldSx, toolbarFilterLabelProps} from '@/pages/components/manage/sys/operation-log/toolbarStyles';

type FeatureFilter = 'Query' | 'Operate' | 'ALL';

function formatTime(v?: string) {
    if (!v) return '-';
    return String(v).replace('T', ' ').substring(0, 19);
}

/**
 * Pretty-print request_summary for Drawer.
 * Revive nested JSON strings (e.g. bsNodes/plNodes) into real objects — no ellipsis.
 */
function reviveJsonDeep(value: unknown): unknown {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (
            (trimmed.startsWith('{') && trimmed.endsWith('}'))
            || (trimmed.startsWith('[') && trimmed.endsWith(']'))
        ) {
            try {
                return reviveJsonDeep(JSON.parse(trimmed));
            } catch {
                return value;
            }
        }
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(reviveJsonDeep);
    }
    if (value && typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            out[k] = reviveJsonDeep(v);
        }
        return out;
    }
    return value;
}

function formatRequestSummaryJson(raw?: string): string {
    if (!raw) return '';
    const text = String(raw);
    try {
        return JSON.stringify(reviveJsonDeep(JSON.parse(text)), null, 2);
    } catch {
        return text;
    }
}

async function copyText(text: string): Promise<boolean> {
    if (!text) return false;
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        try {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);
            return ok;
        } catch {
            return false;
        }
    }
}

/**
 * Display IP region as country-province-city.
 * Accepts stored dash form, raw ip2region pipe form, or legacy concatenated text.
 */
function formatIpLocation(v?: string) {
    if (!v) return '-';
    const s = String(v).trim();
    if (!s) return '-';
    if (/^inner\s*ip$/i.test(s) || s.includes('内网')) return 'Inner IP';
    if (s.includes('|')) {
        const parts = s.split('|');
        // xdb: Country|Province|City|ISP|ISO ; legacy: country|area|province|city|ISP
        const iso = (parts[4] || '').trim();
        const xdbLayout = iso.length === 2 && /^[A-Za-z]{2}$/.test(iso);
        const labels = (xdbLayout
            ? [parts[0], parts[1], parts[2]]
            : [parts[0], parts[2], parts[3]])
            .map(p => (p || '').trim())
            .filter(p => p && p !== '0');
        return labels.length > 0 ? labels.join('-') : '-';
    }
    return s;
}

function formatCompany(row: OperationLogVO) {
    if (row.companyName) return row.companyName;
    if (row.companyId != null) return '#' + String(row.companyId);
    return '-';
}

/**
 * Query vs Operate must follow feature semantics, not HTTP verb alone.
 * WHY: seeded rules like "Query Page" use POST and were mis-bucketed as Operate.
 */
function isQueryOp(row: OperationLogVO) {
    const feature = String(row.featureName || row.action || '').trim();
    // 1. Feature name: Query Page / Query List / View / View Tree ...
    if (/^(query|view)\b/i.test(feature)) {
        return true;
    }
    const uri = String(row.requestUri || '').toLowerCase();
    // 2. URI fallback for POST reads (queryPage / get / list / summary)
    if (/(?:^|\/)(query[\w-]*|get(?:by)?[\w-]*|list[\w-]*|summary)(?:\/|$|\?)/i.test(uri)) {
        return true;
    }
    // 3. Safe GET reads (exclude export/download side-effects)
    const method = String(row.httpMethod || '').toUpperCase();
    return method === 'GET' && !/(export|download)/i.test(uri);
}

function isOperateOp(row: OperationLogVO) {
    return !isQueryOp(row);
}

/** One row for session op detail table; Request summary opens a right Drawer. */
function SessionOpRow({
    row,
    onOpenSummary,
}: {
    row: OperationLogVO;
    onOpenSummary: (row: OperationLogVO) => void;
}) {
    const hasSummary = Boolean(row.requestSummary || row.errorMsg);
    return (
        <TableRow hover>
            <TableCell>{formatTime(row.createTime)}</TableCell>
            <TableCell>{row.pageName || row.module || '-'}</TableCell>
            <TableCell>{row.featureName || row.action || '-'}</TableCell>
            <TableCell sx={{maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                       title={formatCompany(row)}>
                {formatCompany(row)}
            </TableCell>
            <TableCell sx={{maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                       title={row.requestUri || ''}>
                {row.requestUri || '-'}
            </TableCell>
            <TableCell sx={{width: 40, px: 0.5}}>
                {hasSummary ? (
                    <IconButton
                        size="small"
                        aria-label="open-request-summary"
                        onClick={() => onOpenSummary(row)}
                    >
                        <DataObjectOutlined fontSize="small"/>
                    </IconButton>
                ) : null}
            </TableCell>
            <TableCell>
                <Chip size="small"
                      label={row.success === 1 ? 'OK' : 'Fail'}
                      color={row.success === 1 ? 'success' : 'error'}
                      variant="outlined"/>
            </TableCell>
            <TableCell>{row.costMs ?? '-'}</TableCell>
        </TableRow>
    );
}

/**
 * Right-side Drawer: pretty JSON request summary; single click copies full JSON.
 */
function RequestSummaryDrawer({
    open,
    row,
    onClose,
    onCopied,
    onCopyFailed,
}: {
    open: boolean;
    row: OperationLogVO | null;
    onClose: () => void;
    onCopied: () => void;
    onCopyFailed: () => void;
}) {
    const jsonText = useMemo(
        () => formatRequestSummaryJson(row?.requestSummary),
        [row?.requestSummary],
    );

    const handleCopy = async () => {
        if (!jsonText) return;
        const ok = await copyText(jsonText);
        if (ok) onCopied();
        else onCopyFailed();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            // WHY: nest above Session Operations bottom Drawer
            ModalProps={{keepMounted: false}}
            sx={{zIndex: (t) => t.zIndex.modal + 2}}
            PaperProps={{
                sx: {
                    width: {xs: '100%', sm: 480, md: 560},
                    maxWidth: '100vw',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexShrink={0}>
                <Typography variant="h6" fontWeight={700}>Request summary</Typography>
                <Button size="small" onClick={onClose}>Close</Button>
            </Stack>
            <Divider sx={{mb: 1.5, flexShrink: 0}}/>
            {row && (
                <Stack spacing={0.5} mb={1.5} flexShrink={0}>
                    <Typography variant="body2">
                        <b>Feature:</b> {row.featureName || row.action || '-'}
                    </Typography>
                    <Typography variant="body2" sx={{wordBreak: 'break-all'}}>
                        <b>URI:</b> {row.requestUri || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Click JSON below to copy all
                    </Typography>
                </Stack>
            )}
            {row?.errorMsg && (
                <Typography variant="body2" color="error" mb={1.5} flexShrink={0} sx={{whiteSpace: 'pre-wrap'}}>
                    <b>Error:</b> {row.errorMsg}
                </Typography>
            )}
            <Paper
                variant="outlined"
                onClick={handleCopy}
                role="button"
                tabIndex={0}
                aria-label="copy-request-summary-json"
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void handleCopy();
                    }
                }}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    p: 1.5,
                    bgcolor: 'action.hover',
                    overflow: 'auto',
                    cursor: jsonText ? 'pointer' : 'default',
                    '&:hover': jsonText ? {bgcolor: 'action.selected'} : undefined,
                }}
            >
                <Typography
                    component="pre"
                    variant="caption"
                    sx={{
                        m: 0,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        whiteSpace: 'pre',
                        wordBreak: 'normal',
                        overflowWrap: 'normal',
                    }}
                >
                    {jsonText || '-'}
                </Typography>
            </Paper>
        </Drawer>
    );
}

export default function OperationLogList() {
    const theme = useTheme();
    const [data, setData] = useState<OperationLogSessionPage>({
        records: [], total: 0, size: 25, current: 1, pages: 0,
    });
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success',
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [username, setUsername] = useState('');

    const [detailOpen, setDetailOpen] = useState(false);
    const [sessionHeader, setSessionHeader] = useState<OperationLogSessionVO | null>(null);
    const [ops, setOps] = useState<OperationLogVO[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [featureFilter, setFeatureFilter] = useState<FeatureFilter>('Operate');
    const [showRules, setShowRules] = useState(false);
    const [summaryRow, setSummaryRow] = useState<OperationLogVO | null>(null);
    const [summaryOpen, setSummaryOpen] = useState(false);

    const filteredOps = useMemo(() => {
        if (featureFilter === 'ALL') return ops;
        if (featureFilter === 'Query') return ops.filter(isQueryOp);
        return ops.filter(isOperateOp);
    }, [ops, featureFilter]);

    const fetchData = useCallback(async (pageNum = 1, pageSize = 25) => {
        setLoading(true);
        try {
            const res = await OperationLogService.querySessionPage({
                pageNum,
                pageSize,
                username: username.trim() || undefined,
            });
            setData(res);
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Failed to load data';
            setSnackbar({open: true, message, severity: 'error'});
        } finally {
            setLoading(false);
        }
    }, [username]);

    useEffect(() => {
        fetchData(page + 1, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const handleSearch = () => {
        setPage(0);
        fetchData(1, rowsPerPage);
    };

    const openSessionDetail = async (row: OperationLogSessionVO) => {
        setDetailOpen(true);
        setSessionHeader(row);
        setFeatureFilter('Operate');
        setDetailLoading(true);
        setOps([]);
        setSummaryOpen(false);
        setSummaryRow(null);
        try {
            const list = await OperationLogService.listSessionOps(
                row.legacy === 1
                    ? {legacyId: row.legacyId}
                    : {loginToken: row.loginToken},
            );
            setOps(Array.isArray(list) ? list : []);
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Failed to load session ops';
            setSnackbar({open: true, message, severity: 'error'});
        } finally {
            setDetailLoading(false);
        }
    };

    const openRequestSummary = (row: OperationLogVO) => {
        setSummaryRow(row);
        setSummaryOpen(true);
    };

    const closeRequestSummary = () => {
        setSummaryOpen(false);
        setSummaryRow(null);
    };

    const endLabel = (row: OperationLogSessionVO) => {
        if (row.logoutTime) return formatTime(row.logoutTime);
        if (row.closed === 1) return formatTime(row.lastActivityTime);
        return row.legacy === 1 ? '-' : 'Ongoing / Last activity';
    };

    const endValue = (row: OperationLogSessionVO) => {
        if (row.logoutTime) return formatTime(row.logoutTime);
        return formatTime(row.lastActivityTime);
    };

    return (
        <Box sx={{p: 2, height: '100%', boxSizing: 'border-box'}}>
            <Box sx={{position: 'relative', height: 'calc(100vh - 120px)', minHeight: '70vh'}}>
                <Slide direction="left" in={!showRules} mountOnEnter unmountOnExit>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pr: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                       alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700} color="text.primary" sx={{flexShrink: 0}}>
                        Operation Log
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="flex-end" flexWrap="nowrap"
                           sx={{minWidth: 0, overflowX: 'auto', overflowY: 'visible'}}>
                        <InputField size="small" label="Username" value={username}
                                    onChange={e => setUsername(e.target.value)}
                                    InputLabelProps={toolbarFilterLabelProps}
                                    sx={{width: 160, ...toolbarFilterFieldSx}}/>
                        <Button size="small" variant="contained" color="secondary"
                                onClick={handleSearch}>
                            Query
                        </Button>
                        <Button size="small" variant="contained" color="secondary"
                                onClick={() => {
                                    setDetailOpen(false);
                                    setShowRules(true);
                                }}>
                            Rules
                        </Button>
                    </Stack>
                </Stack>

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            minWidth: 960,
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700}}>IP</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Location / Origin</TableCell>
                                <TableCell sx={{fontWeight: 700}}>User</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Ops</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Login</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Logout / Last</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({length: 6}).map((_, i) => (
                                    <TableRow key={i}>
                                        {Array.from({length: 6}).map((__, j) => (
                                            <TableCell key={j}><Skeleton height={24}/></TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : data.records.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{py: 4, color: 'text.secondary'}}>
                                        No data
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.records.map((r, idx) => (
                                    <TableRow
                                        hover
                                        key={r.loginToken || `legacy-${r.legacyId ?? idx}`}
                                        onClick={() => openSessionDetail(r)}
                                        sx={{cursor: 'pointer'}}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={700} component="div">
                                                {r.ipAddress || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                                                   title={formatIpLocation(r.ipLocation)}>
                                            {formatIpLocation(r.ipLocation)}
                                        </TableCell>
                                        <TableCell>{r.realName || r.username || r.userId || '-'}</TableCell>
                                        <TableCell>{r.opCount ?? '-'}</TableCell>
                                        <TableCell>{formatTime(r.loginTime)}</TableCell>
                                        <TableCell title={endLabel(r)}>{endValue(r)}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data.total || 0}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{flexShrink: 0}}
                />

                <Drawer
                    anchor="bottom"
                    open={detailOpen}
                    onClose={() => {
                        setDetailOpen(false);
                        closeRequestSummary();
                    }}
                    PaperProps={{
                        sx: {
                            height: '70vh',
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        },
                    }}
                >
                    <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                           alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={1} flexShrink={0}>
                        <Typography variant="h6" fontWeight={700}>Session Operations</Typography>
                        <InputField
                            size="small"
                            label="Feature"
                            select
                            value={featureFilter}
                            onChange={e => setFeatureFilter(e.target.value as FeatureFilter)}
                            sx={{width: 160, flexShrink: 0}}
                        >
                            <MenuItem value="Operate">Operate</MenuItem>
                            <MenuItem value="Query">Query</MenuItem>
                            <MenuItem value="ALL">ALL</MenuItem>
                        </InputField>
                    </Stack>
                    <Divider sx={{mb: 2, flexShrink: 0}}/>
                    {sessionHeader && (
                        <Stack spacing={0.8} mb={2} flexShrink={0} direction={{xs: 'column', md: 'row'}}
                               flexWrap="wrap" useFlexGap>
                            <Typography variant="body2">
                                <b>IP:</b> {sessionHeader.ipAddress || '-'}
                            </Typography>
                            <Typography variant="body2">
                                <b>Location / Origin:</b> {formatIpLocation(sessionHeader.ipLocation)}
                            </Typography>
                            <Typography variant="body2">
                                <b>User:</b> {sessionHeader.username || '-'} ({sessionHeader.realName || '-'})
                            </Typography>
                            <Typography variant="body2">
                                <b>Login:</b> {formatTime(sessionHeader.loginTime)}
                            </Typography>
                            <Typography variant="body2">
                                <b>Logout / Last:</b> {sessionHeader.logoutTime
                                    ? formatTime(sessionHeader.logoutTime)
                                    : formatTime(sessionHeader.lastActivityTime)}
                                {sessionHeader.closed === 1 ? ' (closed)' : sessionHeader.legacy === 1 ? '' : ' (open)'}
                            </Typography>
                            {sessionHeader.legacy === 1 && (
                                <Chip size="small" label="Legacy single operation" variant="outlined"/>
                            )}
                        </Stack>
                    )}
                    {detailLoading && <Skeleton variant="rectangular" height={200} sx={{flexShrink: 0}}/>}
                    {!detailLoading && filteredOps.length === 0 && (
                        <Typography variant="body2" color="text.secondary" flexShrink={0}>
                            {ops.length === 0 ? 'No operations' : 'No operations for current Feature filter'}
                        </Typography>
                    )}
                    {!detailLoading && filteredOps.length > 0 && (
                        <TableContainer sx={{flex: 1, minHeight: 0, overflow: 'auto'}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 600}}>Time</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Page</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Feature</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Company</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>URI</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Data</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Status</TableCell>
                                        <TableCell sx={{fontWeight: 700}}>ms</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOps.map((op, i) => (
                                        <SessionOpRow
                                            key={op.id ?? `op-${i}`}
                                            row={op}
                                            onOpenSummary={openRequestSummary}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Drawer>

                <RequestSummaryDrawer
                    open={summaryOpen}
                    row={summaryRow}
                    onClose={closeRequestSummary}
                    onCopied={() => setSnackbar({open: true, message: 'JSON copied', severity: 'success'})}
                    onCopyFailed={() => setSnackbar({open: true, message: 'Copy failed', severity: 'error'})}
                />
                    </Box>
                </Slide>

                <Slide direction="right" in={showRules} mountOnEnter unmountOnExit>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pl: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        minHeight: 0,
                    }}>
                        <OperationLogRuleList onBack={() => setShowRules(false)}/>
                    </Box>
                </Slide>

                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({...s, open: false}))}
                />
            </Box>
        </Box>
    );
}
